import { useEffect, useMemo, useState, type CSSProperties } from 'react';

import type { InvitationOpeningExperienceType } from '@tov-plus/domain';
import {
  buildOpeningExperiencePreviewData,
  getOpeningExperienceDefinitions,
  type InvitationAnimationDebugController,
  OpeningExperiencePlayer,
  TovButton,
  msg,
} from '@tov-plus/ui';

type PlaygroundFixtureId =
  | 'portrait-ltr'
  | 'landscape-ltr'
  | 'square-ltr'
  | 'text-heavy-ltr'
  | 'portrait-rtl'
  | 'dark-ltr'
  | 'slow-ready';

const fixtureOptions: Array<{
  dateLabel: string;
  direction: 'ltr' | 'rtl';
  id: PlaygroundFixtureId;
  locationLabel: string;
  themeLabel: string;
  title: string;
  tone: 'light' | 'dark';
}> = [
  {
    dateLabel: msg('pages.open_invitation_playground.fixture_date_garden'),
    direction: 'ltr',
    id: 'portrait-ltr',
    locationLabel: msg(
      'pages.open_invitation_playground.fixture_location_garden',
    ),
    themeLabel: msg('pages.open_invitation_playground.fixture_theme_garden'),
    title: msg('pages.open_invitation_playground.fixture_title_maya_daniel'),
    tone: 'light',
  },
  {
    dateLabel: msg('pages.open_invitation_playground.fixture_date_harbor'),
    direction: 'ltr',
    id: 'landscape-ltr',
    locationLabel: msg(
      'pages.open_invitation_playground.fixture_location_harbor',
    ),
    themeLabel: msg('pages.open_invitation_playground.fixture_theme_modern'),
    title: msg('pages.open_invitation_playground.fixture_title_amelia_rowan'),
    tone: 'light',
  },
  {
    dateLabel: msg('pages.open_invitation_playground.fixture_date_glass'),
    direction: 'ltr',
    id: 'square-ltr',
    locationLabel: msg(
      'pages.open_invitation_playground.fixture_location_glass',
    ),
    themeLabel: msg('pages.open_invitation_playground.fixture_theme_city'),
    title: msg('pages.open_invitation_playground.fixture_title_nora_eli'),
    tone: 'light',
  },
  {
    dateLabel: msg('pages.open_invitation_playground.fixture_date_weekend'),
    direction: 'ltr',
    id: 'text-heavy-ltr',
    locationLabel: msg(
      'pages.open_invitation_playground.fixture_location_weekend',
    ),
    themeLabel: msg('pages.open_invitation_playground.fixture_theme_weekend'),
    title: msg('pages.open_invitation_playground.fixture_title_cohen_levi'),
    tone: 'light',
  },
  {
    dateLabel: msg('pages.open_invitation_playground.fixture_date_hebrew'),
    direction: 'rtl',
    id: 'portrait-rtl',
    locationLabel: msg(
      'pages.open_invitation_playground.fixture_location_jerusalem',
    ),
    themeLabel: msg('pages.open_invitation_playground.fixture_theme_wedding'),
    title: msg('pages.open_invitation_playground.fixture_title_noa_david'),
    tone: 'light',
  },
  {
    dateLabel: msg('pages.open_invitation_playground.fixture_date_midnight'),
    direction: 'ltr',
    id: 'dark-ltr',
    locationLabel: msg(
      'pages.open_invitation_playground.fixture_location_midnight',
    ),
    themeLabel: msg('pages.open_invitation_playground.fixture_theme_black_tie'),
    title: msg('pages.open_invitation_playground.fixture_title_sofia_adrian'),
    tone: 'dark',
  },
  {
    dateLabel: msg('pages.open_invitation_playground.fixture_date_olive'),
    direction: 'ltr',
    id: 'slow-ready',
    locationLabel: msg(
      'pages.open_invitation_playground.fixture_location_olive',
    ),
    themeLabel: msg('pages.open_invitation_playground.fixture_theme_asset'),
    title: msg('pages.open_invitation_playground.fixture_title_lena_tomer'),
    tone: 'light',
  },
];

const viewportOptions = [
  {
    id: 'mobile',
    label: msg('pages.open_invitation_playground.viewport_mobile'),
    width: 390,
    height: 844,
  },
  {
    id: 'desktop',
    label: msg('pages.open_invitation_playground.viewport_desktop'),
    width: 1280,
    height: 820,
  },
  {
    id: 'tablet',
    label: msg('pages.open_invitation_playground.viewport_tablet'),
    width: 820,
    height: 1080,
  },
];

const playbackSpeedOptions = [0.25, 0.5, 1, 2];

function formatPlaybackSpeed(value: number) {
  return String(value).concat('x');
}

export function OpenInvitationPlaygroundPage() {
  const searchParams =
    typeof window === 'undefined'
      ? new URLSearchParams()
      : new URLSearchParams(window.location.search);
  const availableDefinitions = useMemo(
    () =>
      getOpeningExperienceDefinitions().filter(
        (definition) => definition.availableInPhaseOne,
      ),
    [],
  );
  const [experienceType, setExperienceType] =
    useState<InvitationOpeningExperienceType>(
      (searchParams.get('preset') as InvitationOpeningExperienceType | null) ??
        'classic-reveal',
    );
  const [fixtureId, setFixtureId] = useState<PlaygroundFixtureId>(
    (searchParams.get('fixture') as PlaygroundFixtureId | null) ??
      'portrait-ltr',
  );
  const [viewportId, setViewportId] = useState(
    searchParams.get('viewport') ?? 'mobile',
  );
  const [replayKey, setReplayKey] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [reducedMotionPreview, setReducedMotionPreview] = useState(
    searchParams.get('reducedMotion') === '1',
  );
  const [surroundingPreview, setSurroundingPreview] = useState(true);
  const [debugState, setDebugState] = useState({
    currentTime: 0,
    duration: 0,
    phase: 'idle',
  });
  const recordingMode =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('recording') === '1';

  const fixture =
    fixtureOptions.find((entry) => entry.id === fixtureId) ??
    fixtureOptions[0]!;
  const viewport =
    viewportOptions.find((entry) => entry.id === viewportId) ??
    viewportOptions[0]!;
  const resolvedFixture = {
    dateLabel: msg(fixture.dateLabel),
    locationLabel: msg(fixture.locationLabel),
    themeLabel: msg(fixture.themeLabel),
    title: msg(fixture.title),
  };
  const invitationData = buildOpeningExperiencePreviewData(resolvedFixture);
  const controlsDiverged = playbackSpeed !== 1 || reducedMotionPreview;

  useEffect(() => {
    const interval = window.setInterval(() => {
      const debugWindow = window as Window & {
        __tovInvitationAnimationControllers?: Record<
          string,
          InvitationAnimationDebugController
        >;
      };
      const controller =
        debugWindow.__tovInvitationAnimationControllers?.playground;

      if (!controller) {
        return;
      }

      setDebugState({
        currentTime: controller.getCurrentTime(),
        duration: controller.getDuration(),
        phase: controller.getState().phase,
      });
    }, 80);

    return () => window.clearInterval(interval);
  }, []);

  function getController() {
    const debugWindow = window as Window & {
      __tovInvitationAnimationControllers?: Record<
        string,
        InvitationAnimationDebugController
      >;
    };

    return debugWindow.__tovInvitationAnimationControllers?.playground;
  }

  return (
    <main
      className="open-invitation-playground"
      data-recording={recordingMode ? 'true' : 'false'}
      data-testid="open-invitation-playground-page"
    >
      {!recordingMode ? (
        <section className="open-invitation-playground__toolbar">
          <div>
            <p className="page-eyebrow">
              {msg('pages.open_invitation_playground.title')}
            </p>
            <h1>{msg('pages.open_invitation_playground.heading')}</h1>
          </div>
          <label>
            <span>{msg('pages.open_invitation_playground.preset')}</span>
            <select
              data-testid="open-invitation-preset-select"
              onChange={(event) => {
                setExperienceType(
                  event.target.value as InvitationOpeningExperienceType,
                );
                setReplayKey((current) => current + 1);
              }}
              value={experienceType}
            >
              {availableDefinitions.map((definition) => (
                <option key={definition.type} value={definition.type}>
                  {definition.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{msg('pages.open_invitation_playground.fixture')}</span>
            <select
              data-testid="open-invitation-fixture-select"
              onChange={(event) => {
                setFixtureId(event.target.value as PlaygroundFixtureId);
                setReplayKey((current) => current + 1);
              }}
              value={fixtureId}
            >
              {fixtureOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.id}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{msg('pages.open_invitation_playground.viewport')}</span>
            <select
              data-testid="open-invitation-viewport-select"
              onChange={(event) => setViewportId(event.target.value)}
              value={viewportId}
            >
              {viewportOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{msg('pages.open_invitation_playground.speed')}</span>
            <select
              data-testid="open-invitation-speed-select"
              onChange={(event) => setPlaybackSpeed(Number(event.target.value))}
              value={playbackSpeed}
            >
              {playbackSpeedOptions.map((option) => (
                <option key={option} value={option}>
                  {formatPlaybackSpeed(option)}
                </option>
              ))}
            </select>
          </label>
          <label className="open-invitation-playground__check">
            <input
              checked={reducedMotionPreview}
              data-testid="open-invitation-reduced-motion-toggle"
              onChange={(event) => {
                setReducedMotionPreview(event.target.checked);
                setReplayKey((current) => current + 1);
              }}
              type="checkbox"
            />
            <span>
              {msg('pages.open_invitation_playground.reduced_motion')}
            </span>
          </label>
          <label className="open-invitation-playground__check">
            <input
              checked={surroundingPreview}
              onChange={(event) => setSurroundingPreview(event.target.checked)}
              type="checkbox"
            />
            <span>
              {msg('pages.open_invitation_playground.surrounding_page')}
            </span>
          </label>
          <TovButton
            data-testid="open-invitation-replay-button"
            onClick={() => setReplayKey((current) => current + 1)}
            type="button"
          >
            {msg('pages.open_invitation_playground.replay')}
          </TovButton>
          <TovButton
            data-testid="open-invitation-play-pause-button"
            onClick={() => {
              const controller = getController();
              if (debugState.phase === 'playing') {
                controller?.pause();
                return;
              }
              controller?.play();
            }}
            type="button"
            variant="secondary"
          >
            {debugState.phase === 'playing'
              ? msg('pages.open_invitation_playground.pause')
              : msg('pages.open_invitation_playground.play')}
          </TovButton>
          <TovButton
            onClick={() => {
              setPlaybackSpeed(1);
              setReducedMotionPreview(false);
              setReplayKey((current) => current + 1);
            }}
            type="button"
            variant="secondary"
          >
            {msg('pages.open_invitation_playground.reset_to_preset')}
          </TovButton>
          <label className="open-invitation-playground__timeline">
            <span>
              {Math.round(debugState.currentTime)} /{' '}
              {Math.round(debugState.duration)}{' '}
              {msg('pages.open_invitation_playground.milliseconds')}
              {controlsDiverged
                ? ` ${msg('pages.open_invitation_playground.modified')}`
                : ''}
            </span>
            <input
              data-testid="open-invitation-timeline"
              max={debugState.duration || 1}
              min={0}
              onChange={(event) =>
                getController()?.seek(Number(event.target.value))
              }
              step={25}
              type="range"
              value={Math.min(debugState.currentTime, debugState.duration || 1)}
            />
          </label>
        </section>
      ) : null}

      <section className="open-invitation-playground__stage-wrap">
        <div
          className="open-invitation-playground__stage"
          data-fixture-tone={fixture.tone}
          data-reduced-motion-preview={reducedMotionPreview ? 'true' : 'false'}
          data-testid="open-invitation-stage"
          dir={fixture.direction}
          style={
            {
              '--open-invitation-stage-height': `${viewport.height}px`,
              '--open-invitation-stage-width': `${viewport.width}px`,
              '--open-invitation-playback-rate': String(playbackSpeed),
            } as CSSProperties
          }
        >
          {surroundingPreview ? (
            <div className="open-invitation-playground__surrounding">
              <p className="page-eyebrow">{resolvedFixture.themeLabel}</p>
              <h2>{resolvedFixture.title}</h2>
              <p>{resolvedFixture.locationLabel}</p>
            </div>
          ) : null}
          <OpeningExperiencePlayer
            allowSkip={!recordingMode}
            configuration={{
              playbackRate: playbackSpeed,
              reducedMotionPreview,
            }}
            debugControllerId="playground"
            experienceType={experienceType}
            invitationData={invitationData}
            previewMode={!recordingMode}
            replayKey={replayKey}
          />
        </div>
      </section>
    </main>
  );
}
